import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import logo from "../assets/ruempeltaxi-logo.png";

export default function Login() {
  const { user, login, formatApiErrorDetail } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/mitarbeiter"} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(identifier.trim(), password);
    } catch (err) {
      // Differenzierte deutsche Meldungen statt generischer Fallback
      let msg;
      if (!err.response) {
        // Kein Response = Netzwerk-/CORS-Fehler
        msg = "Der Anmeldedienst ist momentan nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung oder versuchen Sie es in Kürze erneut.";
      } else {
        const status = err.response.status;
        const detail = err.response.data?.detail;
        if (status === 401) {
          msg = "Benutzername beziehungsweise E-Mail oder Passwort ist nicht korrekt.";
        } else if (status === 403) {
          msg = detail || "Ihr Benutzerkonto ist derzeit nicht aktiv.";
        } else if (status === 429) {
          msg = detail || "Zu viele Anmeldeversuche. Bitte in 5–10 Minuten erneut versuchen.";
        } else if (status >= 500) {
          msg = "Der Anmeldedienst hat einen internen Fehler gemeldet. Bitte in Kürze erneut versuchen.";
        } else {
          msg = formatApiErrorDetail(detail) || "Die Anmeldung konnte nicht abgeschlossen werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.";
        }
      }
      setError(msg);
      // Diagnose im Browser-Log für den Admin (nicht sichtbar für Nutzer)
      console.warn("[Login] Fehler:", { hasResponse: !!err.response, status: err.response?.status, url: err.config?.url, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-black grain px-4" data-testid="login-page">
      <div className="relative z-10 w-full max-w-md rounded-md border border-dark-gray bg-anthracite p-8">
        <img src={logo} alt="Rümpeltaxi" className="h-10 w-auto" />
        <h1 className="mt-6 font-heading text-2xl font-bold text-white">Anmeldung</h1>
        <p className="mt-2 text-sm text-light-gray/60">Geschützter Bereich – für Verwaltung und Mitarbeiter.</p>

        <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
          <div>
            <label className="mb-2 block text-sm font-semibold text-light-gray" htmlFor="login-identifier">
              Benutzername oder E-Mail
            </label>
            <input
              id="login-identifier"
              type="text"
              required
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="w-full rounded-sm border border-dark-gray bg-brand-black px-4 py-3 text-white outline-none focus:border-yellow"
              data-testid="login-identifier"
              autoComplete="username"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold text-light-gray" htmlFor="login-password">Passwort</label>
            <input
              id="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-sm border border-dark-gray bg-brand-black px-4 py-3 text-white outline-none focus:border-yellow"
              data-testid="login-password"
              autoComplete="current-password"
            />
          </div>
          {error && <p className="rounded-sm bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-sm bg-yellow px-6 py-3.5 font-heading font-bold text-brand-black transition-colors hover:bg-yellow-dark disabled:opacity-60"
            data-testid="login-submit"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            Anmelden
          </button>
        </form>
      </div>
    </div>
  );
}
